import { Alert, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { adminDataGridSx } from '@/components/admin/adminDataGridStyles';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { applicationTypeAsString } from '@/models/ApplicationType';
import { programIdToProgramName } from '@/models/Program';
import { GridLocaleTextOverride } from '@/utils/dataGridUtil';
import { ApplicationDeadlineAdminProps } from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';
import { getStateName } from '@/utils/stateUtil';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { useApplicationDeadline } from '@/hooks/useApplicationDeadline';
import ApplicationDeadlineActions from './ApplicationDeadlineActions';
import EditableDateCell from './EditableDateCell';

type DayjsInput = string | number | Date | null | undefined;

const isDayjsInput = (value: unknown): value is DayjsInput => {
  if (value == null) return true;
  if (typeof value === 'string' || typeof value === 'number') return true;
  return value instanceof Date;
};

const toDateOnlyString = (value: string | number | Date | null | undefined): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const isoDateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch?.[1] != null) return isoDateMatch[1];
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const toGridEditableDateValue = (value: string | number | Date | null | undefined): Date | null => {
  const dateOnly = toDateOnlyString(value);
  if (dateOnly == null) return null;

  // Use local noon so timezone offsets cannot roll the date to the previous/next day.
  return dayjs(`${dateOnly}T12:00:00`).toDate();
};

const getDeadlineOrderValidationError = (row: ApplicationDeadlineAdminProps): string | null => {
  const applicant = toDateOnlyString(row.applicantDeadline);
  const state = toDateOnlyString(row.stateReviewDeadline);

  if (applicant == null || state == null) {
    return 'Applicant and State deadlines are all required before saving.';
  }

  const applicantDate = dayjs(applicant);
  const stateDate = dayjs(state);

  if (!applicantDate.isBefore(stateDate)) {
    return 'Deadlines must be in chronological order: Applicant < State.';
  }

  return null;
};

const AdminApplicationDeadlineDataGrid: React.FC<{
  rows: ApplicationDeadlineAdminProps[];
  loading: boolean;
}> = ({ rows, loading }) => {
  const { locale } = useContext(LocaleContext);
  const { allStates } = useContext(ConfigContext);
  const { setAlert } = useContext(SnackbarContext);
  const { updateApplicationDeadline } = useApplicationDeadline();
  const apiRef = useGridApiRef();
  const [rowData, setRowData] = useState<ApplicationDeadlineAdminProps[]>(rows);
  const [touchedRowKeys, setTouchedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    setRowData(rows);
    setTouchedRowKeys([]);
  }, [rows]);

  const formatDateForDisplay = useCallback((value: unknown): string => {
    if (!isDayjsInput(value) || value == null) return '';

    const dateOnly = toDateOnlyString(value);
    if (dateOnly == null) return '';

    const dateValue = dayjs(dateOnly);
    return dateValue.isValid() ? dateValue.format('LL') : '';
  }, []);

  const normalizeForCompare = useCallback((value: string | Date | null | undefined): string => {
    return toDateOnlyString(value) ?? '';
  }, []);

  const hasDeadlineChanges = useCallback(
    (newRow: ApplicationDeadlineAdminProps, oldRow: ApplicationDeadlineAdminProps): boolean => {
      return (
        normalizeForCompare(newRow.applicantDeadline) !==
          normalizeForCompare(oldRow.applicantDeadline) ||
        normalizeForCompare(newRow.stateReviewDeadline) !==
          normalizeForCompare(oldRow.stateReviewDeadline)
      );
    },
    [normalizeForCompare]
  );

  const getErrorMessage = useCallback((error: unknown): string => {
    return error instanceof Error ? error.message : 'Unknown error';
  }, []);

  const getRowKey = useCallback((row: ApplicationDeadlineAdminProps): string => {
    return `${row.applicationCycle.id}-${row.applicationCycleId ?? 0}-${row.stateId}-${row.applicationCycle.applicationTypeId}`;
  }, []);

  const markRowAsTouched = useCallback(
    (row: ApplicationDeadlineAdminProps) => {
      const rowKey = getRowKey(row);
      setTouchedRowKeys((prev) => (prev.includes(rowKey) ? prev : [...prev, rowKey]));
    },
    [getRowKey]
  );

  const handleSave = useCallback(
    async (row: ApplicationDeadlineAdminProps) => {
      const validationError = getDeadlineOrderValidationError(row);
      if (validationError != null) {
        setAlert({
          message: validationError,
          severity: 'error',
        });
        return;
      }

      const toUtcDateString = (value: string | Date | null | undefined): string | null => {
        const dateOnly = toDateOnlyString(value);
        return dateOnly == null ? null : `${dateOnly}T00:00:00.000Z`;
      };

      const updatedRow: ApplicationDeadlineAdminProps = {
        ...row,
        applicantDeadline: toUtcDateString(row.applicantDeadline),
        stateReviewDeadline: toUtcDateString(row.stateReviewDeadline),
      };

      try {
        const response = await updateApplicationDeadline(updatedRow);
        const rowKey = getRowKey(row);

        setRowData((prev) =>
          prev.map((existingRow) =>
            getRowKey(existingRow) === rowKey
              ? {
                  ...existingRow,
                  ...response,
                }
              : existingRow
          )
        );
        setTouchedRowKeys((prev) => prev.filter((key) => key !== rowKey));
        setAlert({
          message: 'Application deadlines updated successfully',
          severity: 'success',
        });
      } catch (error) {
        setAlert({
          message: `Error updating Application Deadlines. Error: ${getErrorMessage(error)}`,
          severity: 'error',
        });
      }
    },
    [getErrorMessage, getRowKey, setAlert, updateApplicationDeadline]
  );

  const GridToolBarCustom = useCallback((props: GridToolbarContainerProps) => {
    return (
      <GridToolbarContainer {...props}>
        <GridToolbarFilterButton />
        <div style={{ flex: 1 }}></div>

        <div>
          <Alert severity='info' sx={{ my: 1 }}>
            These deadlines are not strictly enforced. They are provided to encourage
            applicants/reviewers to submit on time so materials can be fulfilled.
          </Alert>
        </div>
        <GridToolbarQuickFilter debounceMs={1500} />
      </GridToolbarContainer>
    );
  }, []);

  const columns: GridColDef<ApplicationDeadlineAdminProps>[] = useMemo(() => {
    return [
      {
        field: 'program',
        headerName: 'Program',
        flex: 1.3,
        valueGetter: (_, row) => {
          return programIdToProgramName[row.applicationCycle.programId];
        },
      },
      {
        field: 'applicationType',
        headerName: 'ApplicationType',
        flex: 1.1,
        valueGetter: (_, row) => applicationTypeAsString(row.applicationCycle.applicationTypeId),
      },
      {
        field: 'applicationCycleYear',
        headerName: 'Year',
        flex: 0.5,
        valueGetter: (_, row) => row.applicationCycle.year,
      },
      {
        field: 'stateId',
        headerName: 'State',
        flex: 0.7,
        valueGetter: (value) => getStateName(allStates, value),
      },
      {
        field: 'applicantDeadline',
        headerName: 'Applicant Deadline',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (value) => toGridEditableDateValue(value),
        valueFormatter: (value) => formatDateForDisplay(value),
        cellClassName: 'editable-date-cell',
        renderCell: (params) => <EditableDateCell text={formatDateForDisplay(params.value)} />,
      },
      {
        field: 'stateReviewDeadline',
        headerName: 'State Deadline',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (value) => toGridEditableDateValue(value),
        valueFormatter: (value) => formatDateForDisplay(value),
        cellClassName: 'editable-date-cell',
        renderCell: (params) => <EditableDateCell text={formatDateForDisplay(params.value)} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ApplicationDeadlineAdminProps>) => (
          <ApplicationDeadlineActions
            params={params}
            touchedRowKeys={touchedRowKeys}
            handleSave={handleSave}
          />
        ),
      },
    ];
  }, [allStates, formatDateForDisplay, handleSave, touchedRowKeys]);

  const handleCellClick = useCallback(
    (params: GridCellParams<ApplicationDeadlineAdminProps>) => {
      if (!params.isEditable || apiRef.current == null) return;

      const api = apiRef.current;
      if (api == null) return;

      if (api.getCellMode(params.id, params.field) === 'edit') return;

      api.startCellEditMode({ id: params.id, field: params.field });
    },
    [apiRef]
  );

  return (
    <Box
      style={{
        width: '80vw',
      }}
    >
      {touchedRowKeys.length > 0 && (
        <Box px={2} py={2}>
          <Alert severity='warning'>
            {`You have ${touchedRowKeys.length} unsaved deadline change${
              touchedRowKeys.length === 1 ? '' : 's'
            }. Click Save in the Actions column for each edited row.`}
          </Alert>
        </Box>
      )}
      <Box style={{ height: '60vh', width: '100%' }}>
        <DataGridPremium<ApplicationDeadlineAdminProps>
          apiRef={apiRef}
          showToolbar
          localeText={GridLocaleTextOverride(locale)}
          columns={columns}
          rows={rowData}
          getRowId={getRowKey}
          loading={loading}
          sx={adminAppDeadlineDatagridSx}
          initialState={{
            density: 'compact',
            sorting: {
              sortModel: [{ field: 'applicationCycleYear', sort: 'desc' }],
            },
          }}
          pagination
          onCellClick={handleCellClick}
          processRowUpdate={(newRow, oldRow) => {
            if (!hasDeadlineChanges(newRow, oldRow)) {
              return oldRow;
            }

            markRowAsTouched(newRow);
            setRowData((prev) =>
              prev.map((existingRow) =>
                getRowKey(existingRow) === getRowKey(newRow) ? newRow : existingRow
              )
            );
            return newRow;
          }}
          getRowClassName={(params) =>
            touchedRowKeys.includes(String(params.id)) ? 'app-cycle-unsaved-row' : ''
          }
          onProcessRowUpdateError={(error) => {
            setAlert({
              message: `Error updating deadline row. Error: ${getErrorMessage(error)}`,
              severity: 'error',
            });
          }}
          slots={{
            toolbar: GridToolBarCustom,
          }}
        />
      </Box>
    </Box>
  );
};

const adminAppDeadlineDatagridSx = (theme: Theme) => ({
  ...adminDataGridSx(theme),
  marginLeft: '10px',
  marginRight: '10px',
  flex: '1 1 auto',
});

export default AdminApplicationDeadlineDataGrid;
